/**
 * Dashboard Provider
 * Combines useFilters and useDashboardData hooks
 * Provides context to child components
 */

import { ReactNode, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFilters } from 'src/use-cases/dashboard/useFilters';
import { useDashboardData } from 'src/use-cases/dashboard/useDashboardData';
import { DashboardContext } from './DashboardContext';

interface DashboardProviderProps {
  defaultCurrency: string;
  currencies: string[];
  children: ReactNode;
}

export const DashboardProvider = ({ defaultCurrency, currencies, children }: DashboardProviderProps) => {
  const [searchParams] = useSearchParams();

  // Get filter state + handlers
  const filterState = useFilters({ defaultCurrency });

  // Get dashboard data
  const dataState = useDashboardData({
    filters: filterState.filters,
    page: filterState.page,
    pageSize: filterState.pageSize,
    orderBy: filterState.orderBy,
    orderMethod: filterState.orderMethod,
    keyword: searchParams.get('invoiceId'),
    filterHistory: filterState.filterHistory,
  });

  // Combine into context value (memoized to prevent unnecessary re-renders)
  const contextValue = useMemo(
    () => ({
      // Data
      items: dataState.items,
      totalItems: dataState.totalItems,
      statusStats: dataState.statusStats,
      dataStats: dataState.dataStats,
      allowedCurrencies: dataState.allowedCurrencies,

      // Filter state
      filters: filterState.filters,
      page: filterState.page,
      pageSize: filterState.pageSize,
      orderBy: filterState.orderBy,
      orderMethod: filterState.orderMethod,

      // Prerequisites
      currencies,

      // Handlers
      onFilterChange: filterState.handleFilterChange,
      onTableFilter: filterState.handleTableFilter,
      onStatusChange: filterState.handleStatusChange,
      onPageChange: filterState.handlePageChange,
      onPageSizeChange: filterState.handlePageSizeChange,
      onSort: filterState.handleSort,
      onReset: filterState.handleReset,
      onRefresh: dataState.refetch,

      // Helper functions
      getColumnCounterInfo: filterState.getColumnCounterInfo,

      // Loading states - Granular for progressive loading
      isLoadingInvoiceData: dataState.isLoadingInvoiceData,
      isLoadingChargeData: dataState.isLoadingChargeData,
      isLoadingInvoiceStats: dataState.isLoadingInvoiceStats,
      isLoadingChargeStats: dataState.isLoadingChargeStats,
      isLoadingMetadata: dataState.isLoadingMetadata,
    }),
    [
      // Data dependencies
      dataState.items,
      dataState.totalItems,
      dataState.statusStats,
      dataState.dataStats,

      dataState.allowedCurrencies,
      dataState.isLoadingInvoiceData,
      dataState.isLoadingChargeData,
      dataState.isLoadingInvoiceStats,
      dataState.isLoadingChargeStats,
      dataState.isLoadingMetadata,

      dataState.refetch,

      // Filter state dependencies
      filterState.filters,
      filterState.page,
      filterState.pageSize,
      filterState.orderBy,
      filterState.orderMethod,
      filterState.handleFilterChange,
      filterState.handleTableFilter,
      filterState.handleStatusChange,
      filterState.handlePageChange,
      filterState.handlePageSizeChange,
      filterState.handleSort,
      filterState.handleReset,
      filterState.getColumnCounterInfo,

      // Prerequisites
      currencies,
    ]
  );

  return <DashboardContext.Provider value={contextValue}>{children}</DashboardContext.Provider>;
};
